import { useEffect, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { RoutesApi, type ExploreCampsiteFeature } from '@/shared/api/routes'
import { ApiError } from '@/shared/api/types'

export interface UseDispersedCampsiteLayerProps {
  isEnabled: boolean
  getAccessToken: () => Promise<string | null>
  onLoadingChange?: (isLoading: boolean) => void
  onUpgradeRequired?: () => void
}

const SOURCE_ID = 'dispersed-campsites'
const CLUSTER_LAYER_ID = 'dispersed-campsites-clusters'
const CLUSTER_COUNT_LAYER_ID = 'dispersed-campsites-cluster-count'
const POINT_LAYER_ID = 'dispersed-campsites-points'

const MIN_ZOOM = 9 // Mirror mobile: only fetch at zoom >= 9
const DEBOUNCE_MS = 400
const MAX_FEATURES = 5000 // Bound accumulated features in memory
const GRID_SNAP = 0.05 // ~5km grid for de-duping fetched regions

// Icon images for unclustered points: tent = dead-end (campable) site, turn
// arrows = pullout / turn. Mirrors the visual language of the campsite
// discovery markers (useCampsiteDiscoveryMarkers.ts).
const SITE_ICON_ID = 'dispersed-site-icon'
const TURN_ICON_ID = 'dispersed-turn-icon'

// lucide-react glyph paths (TentTree / ArrowRightLeft), kept in sync with
// useCampsiteDiscoveryMarkers.ts so both surfaces read the same.
const TENT_GLYPH_PATH =
  '<circle cx="4" cy="4" r="2"></circle><path d="m14 5 3-3 3 3"></path><path d="m14 10 3-3 3 3"></path><path d="M17 14V2"></path><path d="M17 14H7l-5 8h20Z"></path><path d="M8 14v8"></path><path d="m9 14 5 8"></path>'
const TURN_GLYPH_PATH =
  '<path d="m16 3 4 4-4 4"></path><path d="M20 7H4"></path><path d="m8 21-4-4 4-4"></path><path d="M4 17h16"></path>'

// Colored circular badge (green/amber) with a white-stroked glyph, matching the
// previous circle colors. Rendered @2x so it stays crisp on retina.
function buildBadgeImage(glyphPath: string, bgColor: string): Promise<HTMLImageElement> {
  const size = 80 // @2x of a 40px logical badge
  const r = size / 2
  const glyphSize = size * 0.5
  const glyphOffset = (size - glyphSize) / 2
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${r}" cy="${r}" r="${r - 4}" fill="${bgColor}" stroke="#ffffff" stroke-width="4"/>` +
    `<svg x="${glyphOffset}" y="${glyphOffset}" width="${glyphSize}" height="${glyphSize}" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${glyphPath}</svg>` +
    `</svg>`
  return new Promise((resolve, reject) => {
    const img = new Image(size, size)
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
  })
}

/**
 * Dispersed camp finder layer for the explore map.
 *
 * Mirrors the mobile experience (campsite_layer_provider.dart): debounced
 * viewport fetches gated to zoom >= 9, features accumulated across pans and
 * deduped by id, loaded regions tracked on a snapped grid to avoid refetching.
 * Points are fed into a Mapbox GeoJSON source with native clustering so
 * clusters break apart into individual sites as the user zooms in.
 */
export function useDispersedCampsiteLayer({
  isEnabled,
  getAccessToken,
  onLoadingChange,
  onUpgradeRequired
}: UseDispersedCampsiteLayerProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const routesApiRef = useRef<RoutesApi | null>(null)
  const featuresMapRef = useRef<Map<string, ExploreCampsiteFeature>>(new Map())
  const loadedRegionsRef = useRef<Set<string>>(new Set())
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const eventListenersAttachedRef = useRef(false)
  const initializedRef = useRef(false)
  const isLoadingRef = useRef(false)
  const blockedRef = useRef(false) // Set after a 403 so we stop hammering the API
  const popupRef = useRef<mapboxgl.Popup | null>(null)

  // Initialize API client
  useEffect(() => {
    if (!routesApiRef.current) {
      routesApiRef.current = new RoutesApi(getAccessToken)
    }
  }, [getAccessToken])

  // Push accumulated features into the GeoJSON source
  const updateSourceData = useCallback(() => {
    if (!map.current) return
    const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (!source) return
    source.setData({
      type: 'FeatureCollection',
      features: Array.from(featuresMapRef.current.values())
    })
  }, [map])

  // Fetch campsites for the current viewport, gated by zoom + region grid
  const fetchCampsites = useCallback(async () => {
    if (!map.current || !routesApiRef.current || blockedRef.current) return

    const zoom = map.current.getZoom()
    if (zoom < MIN_ZOOM) return // Keep accumulated data; just don't fetch more

    const bounds = map.current.getBounds()
    if (!bounds) return

    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const snap = (v: number) => Math.round(v / GRID_SNAP) * GRID_SNAP
    const regionKey = `${Math.floor(zoom)}:${snap(sw.lng)}:${snap(sw.lat)}:${snap(ne.lng)}:${snap(ne.lat)}`
    if (loadedRegionsRef.current.has(regionKey)) return

    const bbox: [number, number, number, number] = [sw.lng, sw.lat, ne.lng, ne.lat]

    if (isLoadingRef.current) return
    isLoadingRef.current = true
    onLoadingChange?.(true)

    try {
      const response = await routesApiRef.current.getExploreCampsites(bbox, zoom)
      loadedRegionsRef.current.add(regionKey)

      if (response?.features?.length) {
        for (const feature of response.features) {
          if (feature.id != null) {
            featuresMapRef.current.set(String(feature.id), feature)
          }
        }
        // Cap memory: drop oldest insertions if we exceed the limit
        if (featuresMapRef.current.size > MAX_FEATURES) {
          const overflow = featuresMapRef.current.size - MAX_FEATURES
          const keys = featuresMapRef.current.keys()
          for (let i = 0; i < overflow; i++) {
            const k = keys.next().value
            if (k !== undefined) featuresMapRef.current.delete(k)
          }
        }
        updateSourceData()
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) {
        blockedRef.current = true
        onUpgradeRequired?.()
      }
      // Other errors: silently ignore; user can pan to retry
    } finally {
      isLoadingRef.current = false
      onLoadingChange?.(false)
    }
  }, [map, onLoadingChange, onUpgradeRequired, updateSourceData])

  // Debounced map move handler
  const handleMapMove = useCallback(() => {
    if (!map.current || !isEnabled || blockedRef.current) return
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchCampsites()
    }, DEBOUNCE_MS)
  }, [map, isEnabled, fetchCampsites])

  // Cluster click -> zoom to expansion zoom
  const handleClusterClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    if (!map.current) return
    const feature = e.features?.[0]
    const clusterId = feature?.properties?.cluster_id
    if (clusterId == null) return
    const source = map.current.getSource(SOURCE_ID) as mapboxgl.GeoJSONSource | undefined
    if (!source) return
    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom == null || !map.current) return
      const coords = (feature!.geometry as GeoJSON.Point).coordinates as [number, number]
      map.current.easeTo({ center: coords, zoom })
    })
  }, [map])

  // Unclustered point click -> popup with road / classification / distance
  const handlePointClick = useCallback((e: mapboxgl.MapLayerMouseEvent) => {
    if (!map.current) return
    const feature = e.features?.[0]
    if (!feature || !feature.properties) return

    const props = feature.properties
    const roadName = props.roadName || 'Unnamed road'
    const classification = props.classification === 'turn' ? 'Pullout / turn' : 'Dead end'
    const distMeters = typeof props.distanceFromMainRoad === 'number' ? props.distanceFromMainRoad : null
    const distMiles = distMeters != null ? (distMeters / 1609.34).toFixed(2) : null
    const landLabel =
      props.landDesignation === 'usfs'
        ? 'National Forest (USFS)'
        : props.landDesignation === 'blm'
          ? 'BLM'
          : null
    const coords = (feature.geometry as GeoJSON.Point).coordinates as [number, number]

    let content = `<div class="p-2 min-w-[180px]">
      <div class="font-semibold text-sm mb-1">${roadName}</div>
      <div class="text-xs text-gray-600 space-y-1">
        <div><span class="font-medium">Type:</span> ${classification}</div>`
    if (landLabel != null) {
      content += `<div><span class="font-medium">Land:</span> ${landLabel}</div>`
    }
    if (distMiles != null) {
      content += `<div><span class="font-medium">From main road:</span> ${distMiles} mi</div>`
    }
    content += `</div></div>`

    if (popupRef.current) {
      popupRef.current.remove()
    }
    popupRef.current = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      anchor: 'bottom',
      offset: [0, -10]
    })
      .setLngLat(coords)
      .setHTML(content)
      .addTo(map.current)
  }, [map])

  // Lazily build + register the point icons when the symbol layer asks for them.
  // Using styleimagemissing keeps this robust across basemap style switches,
  // which wipe registered images.
  const handleStyleImageMissing = useCallback((e: { id: string }) => {
    if (!map.current) return
    const id = e.id
    if (id !== SITE_ICON_ID && id !== TURN_ICON_ID) return
    if (map.current.hasImage(id)) return
    const glyph = id === TURN_ICON_ID ? TURN_GLYPH_PATH : TENT_GLYPH_PATH
    const color = id === TURN_ICON_ID ? '#ca8a04' : '#16a34a'
    buildBadgeImage(glyph, color)
      .then((img) => {
        if (map.current && !map.current.hasImage(id)) {
          map.current.addImage(id, img, { pixelRatio: 2 })
        }
      })
      .catch(() => {
        // Icon build failed; layer falls back to no icon. Non-fatal.
      })
  }, [map])

  const handleMouseEnter = useCallback(() => {
    if (map.current) map.current.getCanvas().style.cursor = 'pointer'
  }, [map])

  const handleMouseLeave = useCallback(() => {
    if (map.current) map.current.getCanvas().style.cursor = ''
  }, [map])

  // Setup / teardown layers + listeners
  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      return
    }

    const teardown = () => {
      if (!map.current) return
      try {
        if (map.current.getLayer(POINT_LAYER_ID)) map.current.removeLayer(POINT_LAYER_ID)
        if (map.current.getLayer(CLUSTER_COUNT_LAYER_ID)) map.current.removeLayer(CLUSTER_COUNT_LAYER_ID)
        if (map.current.getLayer(CLUSTER_LAYER_ID)) map.current.removeLayer(CLUSTER_LAYER_ID)
        if (map.current.getSource(SOURCE_ID)) map.current.removeSource(SOURCE_ID)
      } catch {
        // Ignore if already removed
      }
      featuresMapRef.current.clear()
      loadedRegionsRef.current.clear()
      blockedRef.current = false
      if (popupRef.current) {
        popupRef.current.remove()
        popupRef.current = null
      }
      if (eventListenersAttachedRef.current) {
        map.current.off('styleimagemissing', handleStyleImageMissing)
        map.current.off('moveend', handleMapMove)
        map.current.off('zoomend', handleMapMove)
        map.current.off('click', CLUSTER_LAYER_ID, handleClusterClick)
        map.current.off('click', POINT_LAYER_ID, handlePointClick)
        map.current.off('mouseenter', CLUSTER_LAYER_ID, handleMouseEnter)
        map.current.off('mouseleave', CLUSTER_LAYER_ID, handleMouseLeave)
        map.current.off('mouseenter', POINT_LAYER_ID, handleMouseEnter)
        map.current.off('mouseleave', POINT_LAYER_ID, handleMouseLeave)
        eventListenersAttachedRef.current = false
      }
      initializedRef.current = false
    }

    if (!isEnabled) {
      teardown()
      return
    }

    // Re-create after style switches: getSource is undefined post-switch
    if (initializedRef.current && map.current.getSource(SOURCE_ID)) {
      return
    }

    initializedRef.current = true

    map.current.addSource(SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: Array.from(featuresMapRef.current.values()) },
      cluster: true,
      clusterRadius: 50,
      clusterMaxZoom: 13
    })

    map.current.addLayer({
      id: CLUSTER_LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], '#4ade80', 25, '#22c55e', 100, '#15803d'],
        'circle-radius': ['step', ['get', 'point_count'], 16, 25, 20, 100, 26],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    })

    map.current.addLayer({
      id: CLUSTER_COUNT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    })

    map.current.addLayer({
      id: POINT_LAYER_ID,
      type: 'symbol',
      source: SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': [
          'match',
          ['get', 'classification'],
          'turn', TURN_ICON_ID,
          SITE_ICON_ID // dead_end + fallback
        ],
        'icon-size': 0.8,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      }
    })

    if (!eventListenersAttachedRef.current) {
      map.current.on('styleimagemissing', handleStyleImageMissing)
      map.current.on('moveend', handleMapMove)
      map.current.on('zoomend', handleMapMove)
      map.current.on('click', CLUSTER_LAYER_ID, handleClusterClick)
      map.current.on('click', POINT_LAYER_ID, handlePointClick)
      map.current.on('mouseenter', CLUSTER_LAYER_ID, handleMouseEnter)
      map.current.on('mouseleave', CLUSTER_LAYER_ID, handleMouseLeave)
      map.current.on('mouseenter', POINT_LAYER_ID, handleMouseEnter)
      map.current.on('mouseleave', POINT_LAYER_ID, handleMouseLeave)
      eventListenersAttachedRef.current = true
    }

    // Initial fetch for current viewport
    fetchCampsites()

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [
    map,
    isLoaded,
    styleLoaded,
    isEnabled,
    handleMapMove,
    handleClusterClick,
    handlePointClick,
    handleMouseEnter,
    handleMouseLeave,
    handleStyleImageMissing,
    fetchCampsites
  ])
}
